import type {
  Athlete,
  Attendance,
  AttendanceStats,
  AttendanceStatus,
  GroupJournal,
  GroupSchedule,
  SessionBoard,
} from '@shared/types/api'
import type { AthleteNote } from '@shared/types/athleteNote'
import type { GroupMonthlyPayments } from '@shared/types/monthly_payment'
import type { SportTypeField } from '@shared/api/sportTypesApi'
import type { PlatformKind } from '../platform/platform'

export type {
  Athlete,
  Attendance,
  AttendanceStats,
  AttendanceStatus,
  AthleteNote,
  GroupJournal,
  GroupMonthlyPayments,
  GroupSchedule,
  SessionBoard,
  SportTypeField,
}

/** Шапка любого экрана: в каком клубе и направлении работает пользователь. */
export interface ClubContext {
  clubName: string
  directionName: string
  /** Вид спорта направления: по нему берётся шаблон спортивных полей. */
  sportName: string
}

export interface GroupRef {
  id: string
  name: string
}

/** Вид спорта для мастера (S02): по нему подтягивается шаблон полей. */
export interface SportOption {
  name: string
  /** team | individual - нужно серверу при создании направления */
  kind: string
}

/** Слот расписания группы, который задают в мастере (SC01). */
export interface ScheduleSlot {
  /** 0 = воскресенье */
  day_of_week: number
  start_time: string
  end_time: string
}

/** «Клуб и аккаунт» (S11): клуб, роль, направления и другие клубы пользователя. */
export interface ClubOverview {
  club: { id: string; name: string; role: 'owner' | 'admin' }
  clubs: { id: string; name: string }[]
  directions: { id: string; name: string; sportName: string; current: boolean }[]
}

export interface ChannelState {
  linked: boolean
  account?: string
}

/** Способы входа пользователя (S12). */
export interface Channels {
  telegram: ChannelState
  max: ChannelState
  web: ChannelState
}

/** «Команда и доступы» (S17). */
export interface TeamInfo {
  canManage: boolean
  members: { userId: string; name: string; role: 'owner' | 'admin'; isMe: boolean }[]
  invites: { id: string; code: string; expiresAt: string; remainingUses: number; active: boolean; expired: boolean }[]
}

/** Что ограничивает тариф: ai - запросы Штаба в текущем месяце. */
export type SubscriptionLimitKey = 'members' | 'directions' | 'athletes' | 'groups' | 'coaches' | 'ai'

/**
 * Состояние подписки для экрана. expired - срок прошёл (на сервере статус
 * при этом может остаться active или trial: истечение статус не меняет).
 */
export type SubscriptionState = 'free' | 'trial' | 'active' | 'expired' | 'cancelled'

/** «Подписка клуба» (S19): тариф, срок и использование лимитов. */
export interface SubscriptionSummary {
  /** Тариф, по которому сейчас действуют лимиты: истёкшая подписка - free. */
  tier: string
  status?: string
  expiresAt?: string
  /** max = -1 - без ограничения */
  limits: { key: SubscriptionLimitKey; used: number; max: number }[]
  /** Тариф клуба по записи, даже когда срок истёк: его и продлевают. */
  paidTier: string
  state: SubscriptionState
  autoRenewal: boolean
  /** Карта для автопродления сохранена. Без неё автопродление ничего не списывает. */
  cardSaved: boolean
  /** Владелец клуба: меняет тариф, платит, включает автопродление, вводит промокод. */
  canManage: boolean
  /**
   * Лимиты превышены (после понижения тарифа или конца подписки): через
   * daysLeft дней клуб перестаёт сохранять новые данные (softLocked).
   */
  grace: { softLocked: boolean; daysLeft: number; exceeded: SubscriptionLimitKey[] } | null
  /** Скидка по промокоду: личная, действует на платежи этого аккаунта. */
  discount: { percent: number; code?: string; expiresAt?: string } | null
  /**
   * Клуб уже получал пробный период: он один раз на клуб, дальше платный
   * тариф только с оплатой (organizations.trial_used_at).
   */
  trialUsed: boolean
}

/**
 * Расчёт смены тарифа на сервере (до подтверждения ничего не меняется):
 * - trial - пробный период без оплаты (с FREE или после конца подписки,
 *   один раз на клуб);
 * - free - переход на FREE без оплаты;
 * - payment - оплата картой: 30 дней нового тарифа плюс пересчёт остатка.
 */
export interface TierChangePreview {
  tier: string
  currentTier: string
  kind: 'trial' | 'free' | 'payment'
  /** К оплате, рубли, без скидки промокода: её применяет сервер при создании платежа. */
  charge: number
  /** Цена нового тарифа за 30 дней. */
  price: number
  /** Дней на новом тарифе. 0 - FREE без срока. */
  days: number
  /** Новый срок окончания; null - без срока (FREE). */
  expiresAt: string | null
  /** Неиспользованные дни текущего тарифа и их стоимость, пересчитанные в дни нового. */
  daysRemaining: number
  remainingValue: number
  /** Оплата в пробном периоде: столько его дней закончится, к сроку они не добавляются. */
  trialDaysLost: number
  isUpgrade: boolean
}

export type PaymentStatus = 'pending' | 'succeeded' | 'failed' | 'canceled' | 'unknown'

/** Платёж за тариф (только свои: чужие платежи сервер не отдаёт). */
export interface PaymentInfo {
  id: string
  status: PaymentStatus
  /** Рубли, уже со скидкой промокода. */
  amount: number
  description: string
  createdAt: string
  paidAt?: string
  /** Страница оплаты: пока платёж не завершён, на неё можно вернуться. */
  confirmationUrl?: string
}

/** available: false - оплата картой на сервере не подключена (нет платёжной системы). */
export interface PaymentHistory {
  available: boolean
  items: PaymentInfo[]
}

/** Группа для своей карточки (S06): шапка видна на всех вкладках. */
export interface GroupDetail extends GroupRef {
  /** Слоты недели из groups.schedule. Битое расписание - пустой список. */
  schedule: GroupSchedule[]
  /** Тренеры группы по имени (справочник тренеров направления). */
  coaches: CoachRef[]
  /** Описание группы; пусто - не задано. */
  description: string
}

/** Тренер из справочника направления (не участник клуба: справочник не связан со входом). */
export interface CoachRef {
  id: string
  name: string
}

/** Правка группы (этап 5 шаг 1). Только изменённое; description '' - стереть описание. */
export interface GroupPatch {
  name?: string
  description?: string
}

/**
 * Что станет с одним занятием, если сохранить новое расписание группы:
 * - added - новое занятие;
 * - removed - занятия больше не будет;
 * - moved - в тот же день, но в другое время;
 * - changed - то же начало, другие конец или зал.
 */
export type ScheduleChangeKind = 'added' | 'removed' | 'moved' | 'changed'

export interface ScheduleChangeItem {
  /** YYYY-MM-DD */
  date: string
  change: ScheduleChangeKind
  oldStart?: string
  oldEnd?: string
  oldLocation?: string
  newStart?: string
  newEnd?: string
  newLocation?: string
  /** Занятие прежнего слота уже заведено в базе (отмечали, отменяли). */
  session?: { id: string; cancelled: boolean; marks: number }
  /**
   * Судьба заведённого занятия прежнего слота:
   * - none - в базе его нет, просто не появится;
   * - kept - есть отметки: остаётся разовым, журнал не меняется;
   * - deleted - отметок нет (пустое или отменённое): уберётся.
   */
  outcome: 'none' | 'kept' | 'deleted'
  /** В новом слоте уже есть отменённое занятие: появится отменённым. */
  newCancelled: boolean
  /** Позже двух недель: показываются только заведённые занятия. */
  later: boolean
}

/**
 * Версия расписания группы (этап 3 шаг 10): с даты validFrom действует набор
 * слотов. validFrom null - «с начала»; id null - у группы ещё нет версий.
 */
export interface ScheduleVersion {
  id: string | null
  validFrom: string | null
  schedule: GroupSchedule[]
}

/** Текущее и запланированные расписания группы. */
export interface ScheduleTimeline {
  /** Сегодня, как его принял сервер, YYYY-MM-DD. */
  today: string
  /** «Действует с» по умолчанию: ближайший день без занятия с отметками. */
  defaultFrom: string
  /** Действует сегодня. */
  current: ScheduleVersion
  /** Начнут действовать позже, по возрастанию даты. */
  planned: (ScheduleVersion & { id: string; validFrom: string })[]
}

/** Последствия правки расписания (S06): ничего не изменено, пока не подтверждено. */
export interface SchedulePreview {
  /** Первый и последний день окна предпросмотра, YYYY-MM-DD. from - «Действует с». */
  from: string
  to: string
  /** false - с from расписание и так такое, и запланированного после нет. */
  changed: boolean
  items: ScheduleChangeItem[]
  /** Занятия, которые уберутся: уходят в сохранение как подтверждённый список. */
  deleteSessionIds: string[]
  /** Сколько занятий с отметками выпадут из расписания и останутся разовыми. */
  keptMarked: number
  /** Версия, действующая в день from: от неё «Было», её расписание - ожидаемое при сохранении. */
  base: ScheduleVersion
  /** Запланированные версии с датой >= from, которые сохранение заменит. */
  replaced: (ScheduleVersion & { id: string; validFrom: string })[]
  /** Их id: уходят в сохранение как подтверждённый список. */
  replaceVersionIds: string[]
  /** Есть, только если keptMarked > 0: с этой даты правка отмеченных занятий не задевает. */
  suggestedFrom?: string
}

export interface ScheduleSaveRequest {
  schedule: GroupSchedule[]
  /** Расписание, от которого шла правка: base.schedule из предпросмотра. */
  expected: GroupSchedule[]
  /** «Действует с», YYYY-MM-DD. */
  from: string
  deleteSessionIds: string[]
  /** Запланированные версии из предпросмотра, которые заменяются. */
  replaceVersionIds: string[]
}

export interface ScheduleSaveResult {
  /** Это расписание уже было сохранено (повтор после сбоя): второй раз ничего не сделано. */
  replayed: boolean
  deleted: number
  keptMarked: number
}

/**
 * Одно занятие группы (этап 3 шаг 10, SC13 «только это занятие»): группа,
 * дата и время начала - как ключ экрана занятия.
 */
export interface Occurrence {
  /** YYYY-MM-DD */
  date: string
  /** HH:MM */
  startTime: string
  endTime: string
  location?: string
  /** Занятие заведено в базе. */
  sessionId?: string
  cancelled: boolean
  /** Разовое: вне расписания группы. */
  extra: boolean
  marks: number
}

/** Что сделать с одним занятием. Перенос: toEnd не задан - длительность сохраняется. */
export type OccurrenceChange =
  | { action: 'cancel' }
  | { action: 'restore' }
  | { action: 'move'; toDate: string; toStart: string; toEnd?: string }

/**
 * Почему нельзя:
 * - has_marks - на занятии уже есть отметки (отменить и перенести нельзя, пока их не сняли);
 * - already_cancelled - уже отменено;
 * - not_cancelled - не отменено, возвращать нечего;
 * - cancelled - отменено, переносить нечего;
 * - slot_taken - в это время у группы уже есть занятие;
 * - same_slot - то же время и дата.
 */
export type OccurrenceRefusal =
  | 'has_marks'
  | 'already_cancelled'
  | 'not_cancelled'
  | 'cancelled'
  | 'slot_taken'
  | 'same_slot'

export const OCCURRENCE_REFUSALS: OccurrenceRefusal[] = [
  'has_marks',
  'already_cancelled',
  'not_cancelled',
  'cancelled',
  'slot_taken',
  'same_slot',
]

/** Предпросмотр действия с одним занятием: ничего не изменено. */
export interface OccurrencePreview {
  action: OccurrenceChange['action']
  allowed: boolean
  /** Причина отказа; неизвестный сервер-код приходит как есть. */
  reason?: OccurrenceRefusal | string
  occurrence: Occurrence
  /** Только для переноса: куда. */
  target?: {
    date: string
    startTime: string
    endTime: string
    location?: string
    /** В новом слоте было отменённое занятие: оно вернётся. */
    restoresCancelled: boolean
  }
}

export interface OccurrenceResult {
  /** Повтор: cancel - уже было отменено, restore - и так не отменено. */
  replayed: boolean
  board: SessionBoard
}

export interface OccurrenceMoveResult {
  /** Перенос уже был сделан (повтор после потерянного ответа). */
  replayed: boolean
  /** Ключ нового занятия для экрана занятия. */
  toKey: string
}

// ---------- перекличка текстом и голосом (этап 5, шаг 2) ----------

/** Строка состава в разборе переклички: отметка до разбора и после записи. */
export interface RollCallRow {
  athleteId: string
  name: string
  /** null - без отметки */
  before: AttendanceStatus | null
  beforeNotes?: string
  after: AttendanceStatus | null
  afterNotes?: string
  changed: boolean
}

/**
 * Массовая перекличка меняет и тех, кого не назвали: «остальных нет»,
 * «все, кроме», «все здесь».
 */
export type RollCallMass = 'rest_absent' | 'all_except' | 'all_present'

/**
 * Разобранная фраза переклички (решение владельца 2026-10-06). Короткая
 * перекличка (названы конкретные дети) уже записана - recorded. Массовая
 * («остальных нет», «все, кроме», «все здесь») ничего не пишет: запись -
 * подтверждение действия pendingActionId (confirmAiAction), ровно один раз.
 */
export interface RollCallPreview {
  /** false - во фразе не нашлось ни имён, ни «все здесь». */
  understood: boolean
  /** Весь состав группы по имени. */
  rows: RollCallRow[]
  changes: number
  /** Имена из фразы, которых нет в группе. */
  notFound: string[]
  /** Имена, под которые подходит несколько спортсменов группы. */
  ambiguous: { query: string; candidates: string[] }[]
  mass?: RollCallMass
  /**
   * Массовая перекличка с непонятыми именами: не готовится, подтверждать
   * нечего. При «остальных нет» непонятый получил бы «Нет».
   */
  blocked: boolean
  /** Короткая перекличка: изменения уже записаны, подтверждать нечего. */
  recorded: boolean
  /** Только у массовой: нет - менять нечего или перекличка не готовится. */
  pendingActionId?: string
}

/** Что можно указать при добавлении спортсмена (SC04): минимум, остальное позже. */
export interface NewAthlete {
  name: string
  dateOfBirth?: string
  groupId?: string
  parentPhone?: string
}

/**
 * Изменение карточки. Пустая строка значение не стирает: так работает
 * PUT /athletes/{id}, форма об этом предупреждает.
 */
export interface AthletePatch {
  name?: string
  date_of_birth?: string
  phone?: string
  parent_phone?: string
  weight?: number
  height?: number
  medical_clearance?: string
  notes?: string
  /** '' - убрать из группы */
  group_id?: string
  /** Сливается по ключам: поля, которых нет в запросе, не трогаются. */
  sport_data?: Record<string, string | number>
  /** true - поставить на паузу, false - вернуть */
  paused?: boolean
}

/**
 * Занятие группы в конкретный день: то, что видно в «Сегодня» и в расписании.
 * В реальном API собирается из календаря (`GET /assistants/{id}/calendar`,
 * тип training) и размера группы.
 */
export interface SessionSlot {
  key: string
  groupId: string
  groupName: string
  /** YYYY-MM-DD */
  date: string
  /** HH:MM */
  startTime: string
  /** HH:MM, может быть пустым */
  endTime: string
  athleteCount: number
  cancelled: boolean
  /** Разовое: вне расписания группы (перенос, доп. занятие, осталось от прежнего расписания с отметками). */
  extra?: boolean
}

/** Действие из ответа Штаба: открыть отобранных им спортсменов. */
export interface HubAction {
  type: 'navigate_athletes'
  athleteIds: string[]
  /** Подпись отбора от Штаба: «Не оплатили октябрь» */
  label: string
}

/** Сообщение чата Штаба (S13). */
export interface HubMessage {
  id: string
  role: 'user' | 'assistant'
  /** Текст как есть: экран рендерит его сам, без вставки HTML. */
  text: string
  /** pending и processing - ответ ещё готовится. */
  status: 'pending' | 'processing' | 'completed' | 'failed'
  createdAt: string
  action?: HubAction
  /** Массовые действия из ответа, ждущие подтверждения (S14): карточки под ответом. */
  pendingActionIds?: string[]
}

/**
 * Состояние массового действия Штаба (S14):
 * - pending - ждёт подтверждения, ничего не изменено;
 * - executing - исполняется;
 * - executed, cancelled, expired, failed - итог.
 */
export type AiActionStatus = 'pending' | 'executing' | 'executed' | 'cancelled' | 'expired' | 'failed'

export type AiActionChannel = 'web' | 'telegram' | 'max'

/** Строка точного списка: кто и как изменится. */
export interface AiActionItem {
  label: string
  before: string
  after: string
}

/** Куда ведёт «Открыть» после исполнения. */
export interface AiActionTarget {
  /** group_payments - оплаты группы за месяц; group_schedule - расписание группы; session - занятие; event - событие (своего экрана нет) */
  kind: string
  id: string
  label: string
  date?: string
  startTime?: string
  yearMonth?: string
}

export interface AiActionResultItem {
  label: string
  detail?: string
}

/** Массовое действие или удаление, подготовленное Штабом. */
export interface AiAction {
  id: string
  tool: string
  status: AiActionStatus
  /** У failed: stale (данные изменились), forbidden (права), interrupted, error */
  errorCode?: string
  title: string
  summary: string
  confirmLabel: string
  items: AiActionItem[]
  target?: AiActionTarget
  /** Последствия, которых не видно из списка: что скроется, что останется, можно ли вернуть. */
  note?: string
  /** Удаление: кнопка подтверждения выделена как опасная. */
  danger?: boolean
  result?: { summary: string; done: AiActionResultItem[]; failed: AiActionResultItem[] }
  resolvedChannel?: AiActionChannel
  createdAt: string
  expiresAt: string
  resolvedAt?: string
}

/** Ответ на подтверждение или отмену. */
export interface AiActionDecision {
  action: AiAction
  /** Этот вызов ничего не изменил: показано то, что было решено раньше. */
  replayed: boolean
}

/** Итог отправки вопроса в Штаб. */
/**
 * Объект, о котором пойдёт речь в боте (S16). Занятие - как в маршруте
 * sessions/:key: группа, дата и время.
 */
export type HubContextRef =
  | { kind: 'session'; groupId: string; date: string; startTime: string }
  | { kind: 'group'; groupId: string }
  | { kind: 'athlete'; athleteId: string }

/** Бот Штаба, в который ведёт мини-приложение. */
export type HubBotChannel = 'telegram' | 'max'

/** Код перехода в бота: одноразовый, живёт 15 минут. */
export interface HubContextLink {
  /** ctx_<код> - параметр start ссылки на бота */
  startParam: string
  expiresAt: string
  /** Что бот покажет и добавит к вопросу */
  label: string
}

export interface HubSendResult {
  userMessageId: string
  assistantMessageId: string
  /** Вопрос с этим clientMessageId уже был принят: ничего не создано заново. */
  replayed: boolean
}

// ---------- соревнования, сборы, аттестации (events.md) ----------

export type EventKind = 'competition' | 'training_camp' | 'exam' | 'seminar' | 'other'

/** Статус участия: как в API (`event_participants.status`). */
export type ParticipationStatus = 'registered' | 'confirmed' | 'participated' | 'cancelled' | 'no_show'

/** Взнос участника: нет отметки, оплачено, без оплаты. */
export type ParticipantPayment = 'not_paid' | 'paid' | 'exempt'

/** Дата и необязательное время события в часах устройства. */
export interface EventMoment {
  /** YYYY-MM-DD */
  date: string
  /** HH:MM; пусто - весь день, время не указано */
  time: string
}

/** Соревнование, сборы, аттестация, семинар или другое событие направления. */
export interface ClubEvent {
  id: string
  name: string
  kind: EventKind
  start: EventMoment
  end?: EventMoment
  location?: string
  description?: string
  /** Взнос за участие, рубли, строкой числа ('1500.00'); нет - взноса нет */
  fee?: string
  /** Число участников: есть только в списке событий */
  participantCount?: number
}

/** Поля формы события. Пустая строка стирает значение. */
export interface EventInput {
  name: string
  kind: EventKind
  start: EventMoment
  /** Нет - события в один день */
  end?: EventMoment
  location: string
  description: string
  /** '' - без взноса */
  fee: string
}

export interface EventParticipant {
  athleteId: string
  name: string
  status: ParticipationStatus
  payment: ParticipantPayment
  /** Результат текстом: «1 место» */
  result?: string
}

/** Событие спортсмена с его участием (карточка спортсмена). */
export interface AthleteEventItem {
  event: ClubEvent
  status: ParticipationStatus
  payment: ParticipantPayment
  result?: string
}

/** Изменение участника. result '' стирает результат. */
export interface ParticipantPatch {
  status?: ParticipationStatus
  payment?: ParticipantPayment
  result?: string
}

/** Итог добавления участников: уже бывшие в списке - не ошибка. */
export interface AddParticipantsResult {
  added: string[]
  already: string[]
  /** Сколько не добавлено (удалён, другое направление, сбой по одному) */
  failed: number
}

// ---------- записи и материалы (этап 5 шаг 3, S20, SC17) ----------

/**
 * Запись о спортсмене или группе - как в API (`athlete_notes`, `group_notes`).
 * Читают все тренеры клуба; править, удалять и возвращать из корзины - автор
 * и владелец клуба (запись без автора - только владелец). Удаление - в корзину
 * на 30 дней.
 */
export interface Note {
  id: string
  title: string
  content: string
  /** Тип записи сервера: general, injury, achievement, observation, program (спортсмен), plan (группа) */
  note_type: string
  created_at: string
  updated_at: string
  /** Имя автора; null - автор не сохранён (старые записи) */
  author_name: string | null
  /** Смотрящий может править и удалять: автор или владелец клуба (решает сервер) */
  can_edit: boolean
}

/** Запись в корзине: удалена, через 30 дней удалится насовсем. */
export interface DeletedNote extends Note {
  deleted_at: string
  /** Кто удалил; null - имени нет */
  deleted_by_name: string | null
  /** Когда удалится насовсем */
  purge_at: string
  /** Вернуть может тот, кто мог удалить */
  can_restore: boolean
}

/** Материал в корзине направления; вернуть может любой тренер клуба. */
export interface DeletedMaterial extends Material {
  deleted_at: string
  deleted_by_name: string | null
  purge_at: string
}

/** Поля записи. Заголовок и текст сервер требует непустыми. */
export interface NoteInput {
  title: string
  content: string
  noteType: string
}

export type MaterialCategory = 'requirements' | 'standards' | 'rules' | 'general'

/** Материал направления (`materials`): текст с разделом и тегами. Файлов и ссылок отдельно нет. */
export interface Material {
  id: string
  title: string
  content: string
  category: MaterialCategory
  tags: string[]
  /** Порядок внутри раздела: правка его не трогает */
  sort_order: number
  created_at: string
  updated_at: string
}

/** Поля материала. sortOrder - прежний порядок при правке (сервер ставит то, что пришло). */
export interface MaterialInput {
  title: string
  content: string
  category: MaterialCategory
  tags: string[]
  sortOrder?: number
}

/**
 * Контракт данных среза «Сегодня → занятие → посещаемость».
 *
 * Отличие от текущего API одно: getBoard только читает занятие. Сейчас
 * просмотр идёт через `POST /training-sessions/resolve`, который заводит строку
 * занятия даже при простом открытии; для настоящей реализации нужна ручка
 * чтения без создания (см. docs/design/redesign-v1/slice.md).
 */
export interface SliceApi {
  getContext(): Promise<ClubContext>
  /** Занятия за период включительно, отсортированы по дате и времени. */
  getSessions(from: string, to: string): Promise<SessionSlot[]>
  getBoard(key: string): Promise<SessionBoard>
  /** status = null снимает отметку. Ответ - доска после сохранения. */
  mark(key: string, athleteId: string, status: AttendanceStatus | null): Promise<SessionBoard>

  // Спортсмены (этап 2, S07-S09)
  listGroups(): Promise<GroupRef[]>
  listAthletes(): Promise<Athlete[]>
  getAthlete(id: string): Promise<Athlete>
  createAthlete(input: NewAthlete): Promise<Athlete>
  /**
   * Возможные дубли перед созданием (SC04): спортсмены направления, похожие по
   * имени. Сопоставляет сервер - с уменьшительными и порядком слов.
   */
  findSimilarAthletes(name: string): Promise<Athlete[]>
  updateAthlete(id: string, patch: AthletePatch): Promise<Athlete>
  deleteAthlete(id: string): Promise<void>
  /** Шаблон спортивных полей вида спорта направления. */
  getSportFields(sportName: string): Promise<SportTypeField[]>
  getAthleteAttendance(id: string, from: string, to: string): Promise<{ items: Attendance[]; stats: AttendanceStats }>
  /** Записи о спортсмене, новые первыми (до 100). */
  listAthleteNotes(id: string): Promise<Note[]>
  addAthleteNote(id: string, input: NoteInput): Promise<Note>

  // Группы (этап 2, S06, S10)
  getGroup(id: string): Promise<GroupDetail>
  /** Состав группы сейчас. */
  listGroupAthletes(id: string): Promise<Athlete[]>
  /** Журнал за период включительно. */
  getGroupJournal(id: string, from: string, to: string): Promise<GroupJournal>
  /** Отметки оплат за месяц YYYY-MM. */
  getGroupPayments(id: string, yearMonth: string): Promise<GroupMonthlyPayments>
  /** Поставить или снять отметку оплаты. Повтор не создаёт вторую запись (upsert). */
  setPayment(groupId: string, athleteId: string, yearMonth: string, paid: boolean): Promise<void>
  /** Что изменится, если сохранить это расписание. Ничего не меняет. */
  previewGroupSchedule(groupId: string, schedule: GroupSchedule[], from: string): Promise<SchedulePreview>
  /**
   * Сохранить расписание после предпросмотра (этап 3 шаг 8). Расписание успели
   * изменить в другом месте - SliceApiError kind 'conflict', message
   * 'schedule_changed'; занятия группы изменились после предпросмотра -
   * 'preview_stale'. Повтор после сбоя безопасен: уже сохранённое расписание
   * второй раз не применяется (replayed).
   */
  saveGroupSchedule(groupId: string, req: ScheduleSaveRequest): Promise<ScheduleSaveResult>
  /** Текущее и запланированные расписания группы (этап 3 шаг 10). today - сегодня у тренера. */
  getGroupScheduleTimeline(groupId: string, today: string): Promise<ScheduleTimeline>

  // Одно занятие (этап 3 шаг 10, SC13). key - как sessionKey(groupId, date, startTime).
  /** Можно ли и что именно изменится. Ничего не меняет. */
  previewOccurrence(key: string, change: OccurrenceChange): Promise<OccurrencePreview>
  /**
   * Отменить только это занятие. Есть отметки - SliceApiError('has_marks',
   * 'conflict'). Уже отменено - replayed, второй раз ничего не делается.
   */
  cancelOccurrence(key: string): Promise<OccurrenceResult>
  /** Вернуть отменённое занятие. Не отменено - replayed. */
  restoreOccurrence(key: string): Promise<OccurrenceResult>
  /**
   * Перенести только это занятие: исходное отменяется, на новое время
   * заводится разовое. Отказ - SliceApiError kind 'conflict' с кодом
   * has_marks, slot_taken или cancelled; то же время - SliceApiError
   * ('same_slot', 'server'). Повтор после потерянного ответа - replayed.
   */
  moveOccurrence(key: string, to: { date: string; startTime: string; endTime?: string }): Promise<OccurrenceMoveResult>

  // Перекличка текстом и голосом на экране занятия (этап 5, шаг 2)
  /**
   * Разобрать фразу переклички: весь состав «было -> станет» и непонятые
   * имена. Короткую перекличку сразу записывает (recorded), массовую только
   * готовит к подтверждению. Запрос Штаба не тратит. Подписка закончилась -
   * SliceApiError('soft_locked', 'limit'); слишком часто -
   * SliceApiError('rate_limited', 'server'); отметки изменились между
   * разбором и записью - SliceApiError('roll_call_stale', 'conflict'); модель
   * не ответила - SliceApiError('parse_failed', 'server').
   */
  previewRollCall(key: string, text: string): Promise<RollCallPreview>
  /**
   * Распознать голос переклички. Подсказка распознавания - состав группы
   * занятия. Пустая строка - слов не разобрано.
   */
  transcribeRollCall(key: string, audio: Blob): Promise<string>

  // Клуб и аккаунт (этап 2, S11, S12, S17, S19)
  getClubOverview(): Promise<ClubOverview>
  switchClub(id: string): Promise<void>
  switchDirection(id: string): Promise<void>
  getChannels(): Promise<Channels>
  /** Код для привязки: вводится в другом мессенджере или в вебе. Живёт 10 минут. */
  createLinkCode(): Promise<{ code: string; expiresInMinutes: number }>
  getTeam(): Promise<TeamInfo>
  /** Приглашение создаётся только этим вызовом, после подтверждения в интерфейсе. */
  createInvite(validDays: number, maxUses: number): Promise<{ code: string }>
  revokeInvite(id: string): Promise<void>
  getSubscription(): Promise<SubscriptionSummary>

  // Тариф и оплата (S19, SC18)
  /** Расчёт смены на тариф каталога. Ничего не меняет. */
  previewTierChange(tier: string): Promise<TierChangePreview>
  /**
   * Бесплатная смена: пробный период или переход на FREE. Сервер считает
   * заново; если смена стоит денег - SliceApiError('payment_required', 'conflict').
   * Не владелец - SliceApiError('forbidden', 'server').
   */
  applyFreeTierChange(tier: string): Promise<void>
  /**
   * Создать платёж за смену или продление. returnUrl - куда платёжная
   * страница вернёт после оплаты ('' - адрес по умолчанию сервера). Оплата
   * не подключена - SliceApiError('payments_unavailable', 'server').
   */
  /**
   * Платёж за смену или продление. Пока прежний платёж за тот же тариф ждёт
   * банка, второй не создаётся: с теми же условиями сервер вернёт прежний
   * (reused), с другими - SliceApiError 'payment_pending' с paymentId.
   */
  createTierPayment(
    tier: string,
    opts: { autoRenewal: boolean; returnUrl: string },
  ): Promise<{ paymentId: string; confirmationUrl: string; reused?: boolean }>
  /** Статус платежа. withProvider - спросить платёжную систему напрямую (если уведомление не дошло). */
  getPayment(id: string, withProvider?: boolean): Promise<PaymentInfo>
  listPayments(): Promise<PaymentHistory>
  setAutoRenewal(on: boolean): Promise<void>
  /**
   * Проверить и применить промокод. Недействительный - SliceApiError с
   * причиной для человека в message и kind 'conflict'.
   */
  redeemPromo(code: string): Promise<{ percent: number; expiresAt: string }>

  // Мастер первого запуска (этап 2, S01, S02, SC01)
  renameClub(name: string): Promise<void>
  /** Что даёт приглашение - до вступления (S01: «вы вступаете в клуб X»). */
  previewInvite(code: string): Promise<{ clubName: string }>
  /** Вступить по коду и переключиться в этот клуб. */
  acceptInvite(code: string): Promise<void>
  listSportTypes(): Promise<SportOption[]>
  /** Создать направление и сделать его текущим. */
  createDirection(name: string, sport: SportOption): Promise<{ id: string }>
  /**
   * Создать группу в текущем направлении. Лимит тарифа - SliceApiError
   * ('group limit reached', 'limit'), подписка истекла - ('soft_locked', 'limit').
   */
  createGroup(name: string, schedule: ScheduleSlot[], description?: string): Promise<{ id: string }>

  // Штаб в PWA (этап 3, S13, SC09)
  /** Разговор в текущем направлении, старые первыми. */
  getHubHistory(): Promise<HubMessage[]>
  /**
   * Поставить вопрос Штабу. clientMessageId выдаёт клиент один раз на
   * сообщение: повтор с ним после сбоя не создаёт второй вопрос и не тратит
   * лимит. Исчерпанный лимит - SliceApiError kind 'limit'.
   *
   * assistantId - направление вопроса из очереди на устройстве (этап 4): он
   * уходит туда, где задан, даже если тренер с тех пор переключился. Без него -
   * текущее направление.
   */
  sendHubMessage(text: string, clientMessageId: string, assistantId?: string): Promise<HubSendResult>
  /** Дождаться готового ответа. signal прекращает ожидание (уход с экрана). */
  waitHubReply(assistantMessageId: string, signal?: AbortSignal): Promise<HubMessage>
  /**
   * Распознать запись голоса в текст. Пустая строка - слов не разобрано.
   * assistantId - как у sendHubMessage: подсказка распознавания по составу
   * направления, где записан вопрос.
   */
  transcribeVoice(audio: Blob, assistantId?: string): Promise<string>

  // Безопасные массовые действия (этап 3, S14, SC08)
  /** Снимок действия: точный список и состояние. Чужое или недоступное - not_found. */
  getAiAction(id: string): Promise<AiAction>
  /**
   * Подтвердить. Исполняется на сервере ровно один раз: повтор после сбоя
   * безопасен и возвращает сохранённый итог с replayed: true.
   */
  confirmAiAction(id: string): Promise<AiActionDecision>
  /** Отменить ожидающее действие. Уже решённое не меняется. */
  cancelAiAction(id: string): Promise<AiActionDecision>

  // Переход из мини-приложения в бота с контекстом (этап 3, S16)
  /**
   * Код контекста для ссылки на бота. Без ref - только клуб и направление.
   * Чужой или удалённый объект - not_found. Код одноразовый: на каждый переход
   * новый.
   */
  createHubContextLink(channel: HubBotChannel, ref?: HubContextRef): Promise<HubContextLink>

  // Соревнования, сборы, аттестации (events.md)
  /** Все события направления с числом участников. */
  listEvents(): Promise<ClubEvent[]>
  /** Удалённое или чужое - not_found. */
  getEvent(id: string): Promise<ClubEvent>
  createEvent(input: EventInput): Promise<ClubEvent>
  /** Все поля формы: пустые место, описание, окончание и взнос стираются. */
  updateEvent(id: string, input: EventInput): Promise<ClubEvent>
  deleteEvent(id: string): Promise<void>
  /** Участники по имени; удалённые спортсмены не входят. */
  listEventParticipants(id: string): Promise<EventParticipant[]>
  /**
   * Добавить участников. Повтор безопасен: уже бывшие в списке приходят в
   * already, второй строки сервер не создаёт.
   */
  addEventParticipants(eventId: string, athleteIds: string[]): Promise<AddParticipantsResult>
  updateEventParticipant(eventId: string, athleteId: string, patch: ParticipantPatch): Promise<void>
  /** Убрать из события. Уже убранный - не ошибка. */
  removeEventParticipant(eventId: string, athleteId: string): Promise<void>
  /** События спортсмена с его статусом. */
  listAthleteEvents(athleteId: string): Promise<AthleteEventItem[]>

  // Группы целиком (этап 5 шаг 1, SC13)
  /** Название и описание. Расписание правится отдельно (saveGroupSchedule). */
  updateGroup(id: string, patch: GroupPatch): Promise<void>
  /**
   * Удалить группу (мягко: спортсмены остаются без группы, посещения и
   * отметки оплат - в базе). Уже удалённая - not_found.
   */
  deleteGroup(id: string): Promise<void>
  /** Справочник тренеров текущего направления, по имени. */
  listCoaches(): Promise<CoachRef[]>
  /** Новый тренер в справочник. Лимит тарифа - SliceApiError('coach limit reached', 'limit'). */
  createCoach(name: string): Promise<CoachRef>
  /** Назначить (assigned) или снять тренера группы. Повтор безопасен. */
  setGroupCoach(groupId: string, coachId: string, assigned: boolean): Promise<void>

  // Записи и материалы (этап 5 шаг 3, S20, SC17). Удалённая (в корзине) или
  // чужая запись и материал - not_found: повтор удаления после потерянного
  // ответа - тоже. Подписка закончилась - SliceApiError('soft_locked', 'limit').
  // Чужая запись (не автор и не владелец клуба) - SliceApiError('not_note_author', 'forbidden').
  updateAthleteNote(athleteId: string, noteId: string, input: NoteInput): Promise<Note>
  /** В корзину на 30 дней. */
  deleteAthleteNote(athleteId: string, noteId: string): Promise<void>
  /** Корзина спортсмена: удалённые за 30 дней, свежие первыми. */
  listAthleteNotesTrash(athleteId: string): Promise<DeletedNote[]>
  /** Вернуть из корзины; уже возвращённая - та же запись; срок вышел - not_found. */
  restoreAthleteNote(athleteId: string, noteId: string): Promise<Note>
  /** Записи о группе, новые первыми (до 100). */
  listGroupNotes(groupId: string): Promise<Note[]>
  addGroupNote(groupId: string, input: NoteInput): Promise<Note>
  updateGroupNote(groupId: string, noteId: string, input: NoteInput): Promise<Note>
  /** В корзину на 30 дней. */
  deleteGroupNote(groupId: string, noteId: string): Promise<void>
  listGroupNotesTrash(groupId: string): Promise<DeletedNote[]>
  restoreGroupNote(groupId: string, noteId: string): Promise<Note>
  /** Материалы текущего направления: по разделу и порядку. */
  listMaterials(): Promise<Material[]>
  getMaterial(id: string): Promise<Material>
  /** В текущее направление. */
  createMaterial(input: MaterialInput): Promise<Material>
  updateMaterial(id: string, input: MaterialInput): Promise<Material>
  /** В корзину на 30 дней: Штаб перестаёт на него опираться сразу. */
  deleteMaterial(id: string): Promise<void>
  /** Корзина текущего направления. */
  listMaterialsTrash(): Promise<DeletedMaterial[]>
  /** Вернуть материал; уже возвращённый - тот же; срок вышел - not_found. */
  restoreMaterial(id: string): Promise<Material>
  // Направления, справочник тренеров, мелочи клуба (этап 5 шаг 4).
  // Новое направление вне мастера - тот же createDirection (становится
  // текущим); лимит тарифа - SliceApiError('direction_limit_reached', 'limit').
  /** Переименовать направление или сменить вид спорта. Удалённое - not_found. */
  updateDirection(id: string, patch: DirectionPatch): Promise<void>
  /** Что есть в направлении: для предпросмотра удаления и смены вида спорта. Ничего не меняет. */
  getDirectionContents(id: string): Promise<DirectionContents>
  /**
   * Удалить направление вместе с его группами, спортсменами и справочником
   * тренеров (мягко). Удалено текущее - текущим становится другое направление
   * клуба, а если других нет - никакое (откроется мастер). Уже удалённое -
   * not_found.
   */
  deleteDirection(id: string): Promise<void>
  /** Справочник тренеров текущего направления с контактами и группами, по имени. */
  listCoachCards(): Promise<CoachCard[]>
  /** Тренер справочника. Удалённый - not_found. */
  getCoach(id: string): Promise<CoachCard>
  /** Новый тренер с контактами. Лимит тарифа - SliceApiError('coach limit reached', 'limit'). */
  createCoachCard(input: { name: string; phone?: string; email?: string }): Promise<CoachRef>
  /** Только изменённое; phone или email '' - стереть. */
  updateCoach(id: string, patch: CoachPatch): Promise<void>
  /** Убрать из справочника (и из групп). Уже удалённый - not_found. */
  deleteCoach(id: string): Promise<void>
  /**
   * Выйти из текущего клуба (не владелец) и перейти в другой свой клуб.
   * Другого клуба нет - SliceApiError('last_club', 'conflict').
   */
  leaveClub(): Promise<{ switchedTo: string }>
  /**
   * Удалить текущий клуб (владелец, других участников нет) и перейти в другой.
   * Другого клуба нет - ('last_club', 'conflict'); есть участники -
   * ('has_members', 'conflict'); не владелец - ('forbidden', 'server').
   */
  deleteClub(): Promise<{ switchedTo: string }>
  // ---------- обратная связь как переписка ----------
  /** Мои обращения, свежие по последнему сообщению первыми. */
  listFeedbackThreads(): Promise<FeedbackThreadSummary[]>
  /** Переписка целиком; чужое или удалённое - SliceApiError('not found', 'not_found'). */
  getFeedbackThread(id: string): Promise<FeedbackThread>
  /**
   * Новое обращение команде ProCoach. from - где открыто приложение. Ответ
   * поддержки виден в треде всегда; уведомление уходит туда, откуда было
   * последнее сообщение (из приложения - только отметка «новый ответ»).
   * Слишком длинное - SliceApiError('too_long', 'server').
   */
  createFeedbackThread(message: string, from: PlatformKind): Promise<FeedbackThread>
  /** Ответ в свою переписку (то же обращение, не новое). */
  replyFeedbackThread(id: string, message: string, from: PlatformKind): Promise<FeedbackMessage>
  /** Отметить ответы поддержки прочитанными. */
  markFeedbackRead(id: string): Promise<void>
}

// ---------- направления, тренеры, мелочи клуба (этап 5 шаг 4) ----------

/** Правка направления: только изменённое. */
export interface DirectionPatch {
  name?: string
  /** Новый вид спорта: шаблон спортивных полей берётся из него. */
  sport?: SportOption
}

/** Содержимое направления: что пропадёт при удалении. */
export interface DirectionContents {
  groups: { id: string; name: string; athletes: number }[]
  /** Все спортсмены направления по имени, и на паузе тоже. */
  athletes: { id: string; name: string }[]
  coaches: CoachRef[]
  /** Соревнования, сборы и другие события направления. */
  events: number
}

/** Тренер справочника направления: контакт и группы, которые ведёт. */
export interface CoachCard extends CoachRef {
  phone: string
  email: string
  groups: GroupRef[]
}

/** Правка тренера: только изменённое; '' у phone и email - стереть. */
export interface CoachPatch {
  name?: string
  phone?: string
  email?: string
}

// ---------- обратная связь как переписка ----------

/** Откуда сообщение пользователя: бот Telegram, бот MAX или приложение. */
export type FeedbackChannel = 'telegram' | 'max' | 'app'

/** Обращение в списке. */
export interface FeedbackThreadSummary {
  id: string
  subject: string
  /** new, in_review, in_dialog, resolved, closed */
  status: string
  createdAt: string
  lastAt: string
  lastFrom: 'user' | 'support'
  lastText: string
  /** Непрочитанные ответы поддержки: бейдж «Новый ответ». */
  unread: number
}

/** Сообщение переписки. */
export interface FeedbackMessage {
  id: string
  from: 'user' | 'support'
  text: string
  createdAt: string
  /** У сообщений пользователя: откуда написано. */
  channel?: FeedbackChannel
  /** У сообщений из приложения: где оно было открыто. */
  platform?: PlatformKind
  /** У ответов поддержки: куда о нём сообщили. */
  notifyChannel?: FeedbackChannel
  notifyStatus?: 'pending' | 'sent' | 'failed' | 'not_needed'
  readAt?: string
}

/** Обращение целиком. */
export interface FeedbackThread {
  id: string
  subject: string
  status: string
  createdAt: string
  messages: FeedbackMessage[]
}

/**
 * Ошибка слоя данных. Для kind 'limit' в message код причины: 'ai_limit_exceeded'
 * (кончились запросы Штаба за месяц), 'soft_locked' (подписка истекла, клуб
 * только для чтения) или текст сервера для лимитов тарифа.
 */
export class SliceApiError extends Error {
  constructor(
    message: string,
    /**
     * limit - упёрлись в правило тарифа или подписки: повтор не поможет, нужен другой путь;
     * conflict - данные изменились, пока пользователь смотрел: нужно перечитать и сверить;
     * forbidden - правило прав: сделать это может другой человек (автор записи, владелец клуба).
     */
    readonly kind: 'network' | 'server' | 'not_found' | 'limit' | 'conflict' | 'forbidden',
    /** payment_pending: незавершённый платёж, из-за которого новый не создан. */
    readonly paymentId?: string,
  ) {
    super(message)
    this.name = 'SliceApiError'
  }
}
